"use client";

import type React from "react";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  Clock,
  Calendar,
  Users,
  BookOpen,
  Send,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { usePostHog } from "posthog-js/react";
import { container, item } from "@/lib/framer";
import { FaFacebook, FaInstagram, FaTwitter, FaYoutube } from "react-icons/fa";
import { z } from "zod";
import { feedbackFormSchema, FeedbackFormValues } from "@/lib/schemas";
import { useForm } from "react-hook-form";
import { customZodResolver } from "@/lib/schemas";
import {
  FormControl,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { GeneralInquiryForm } from "@/components/landing/contact";
import { SupportContactForm } from "@/components/support/support-contact-form";
// import CalendarComponent from "../calendar"

export default function ContactPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <section className="relative bg-muted py-8 md:py-10">
        <div className="container mx-auto px-4">
          <div className="mb-6">
            <Link
              href="/"
              className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </div>
          <div className="grid gap-8 md:grid-cols-2 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl font-bold tracking-tight mb-4">
                Contact Us
              </h1>
              <p className="text-muted-foreground mb-6">
                We&apos;re here to help! Whether you have questions about
                admissions, want to schedule a visit, or need general
                information about our school, our team is here to help.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild>
                  <a href="#contact-form">Send a Message</a>
                </Button>
                <Button variant="outline" asChild>
                  <a href="#visit-us">Visit Our School</a>
                </Button>
              </div>
            </motion.div>
            <motion.div
              className="relative h-[300px] md:h-[400px] overflow-hidden rounded-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Image
                src="https://res.cloudinary.com/ds1npuv1v/image/upload/v1742045620/contact_ywirm8.jpg"
                alt="School building entrance"
                fill
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                <p className="text-white text-lg font-medium">
                  Our doors are always open to the community
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* <CalendarComponent /> */}

      {/* Contact Information Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Get in Touch
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Here are the different ways you can reach us. We strive to respond
              to all inquiries within 24-48 hours during school days.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -10 }}
            >
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>Visit Us</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Our campus is located in a peaceful area of Pokhara, with
                    easy access from the city center.
                  </p>
                  <div className="space-y-2">
                    <p className="font-medium">Address:</p>
                    <p className="text-muted-foreground">
                      Shree Janakalyan Boarding School
                      <br />
                      Pokhara-23, Kaski
                      <br />
                      Gandaki Province, Nepal
                    </p>
                  </div>
                  <Button variant="outline" className="mt-6 w-full" asChild>
                    <a href="#map">View on Map</a>
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -10 }}
            >
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>Call Us</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Our office is open from Monday to Friday, 8:00 AM to 4:00
                    PM, and Saturday from 8:00 AM to 12:00 PM.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <p className="font-medium">Main Office:</p>
                      <p className="text-muted-foreground">+977 9805833235</p>
                    </div>
                    <div>
                      <p className="font-medium">Admissions:</p>
                      <p className="text-muted-foreground">+977 9846042783</p>
                    </div>
                    <div>
                      <p className="font-medium">Principal&apos;s Office:</p>
                      <p className="text-muted-foreground">+977 9808718289</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              whileHover={{ y: -10 }}
            >
              <Card className="h-full">
                <CardHeader className="pb-2">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>Email Us</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Send us an email and we&apos;ll get back to you as soon as
                    possible. Please allow 24-48 hours for a response.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <p className="font-medium">General Inquiries:</p>
                      <p className="text-muted-foreground">
                        janakalyanboardingschool@gmail.com
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Admissions:</p>
                      <p className="text-muted-foreground">
                        admin@janakalyanboardingschool.edu.np
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Principal&apos;s Office:</p>
                      <p className="text-muted-foreground">
                        krishna@janakalyanboardingschool.edu.np
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Office Hours Section */}
      <section id="visit-us" className="py-16 md:py-24 bg-muted">
        <div className="container mx-auto px-4">
          <div className="grid gap-8 md:grid-cols-2 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold tracking-tight mb-6">
                Office Hours & Visiting Information
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Clock className="mt-1 h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-2">Office Hours</h3>
                    <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                      <div>Sunday - Friday:</div>
                      <div>10:00 AM - 4:00 PM</div>
                      <div>Saturday:</div>
                      <div>Closed</div>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Calendar className="mt-1 h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-2">School Year</h3>
                    <p className="text-muted-foreground">
                      Our academic year runs from Baisakh to Chaitra, with
                      breaks during government holidays.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Users className="mt-1 h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-2">Campus Tours</h3>
                    <p className="text-muted-foreground">
                      We offer guided tours of our campus for prospective
                      students and their families. Tours are available Monday
                      through Friday from 10:00 AM to 2:00 PM by appointment.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <BookOpen className="mt-1 h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-2">Admissions Office</h3>
                    <p className="text-muted-foreground">
                      Our admissions office is open Sunday through Friday from
                      10:00 AM to 2:00 PM. We recommend scheduling an
                      appointment for admissions inquiries.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-[400px] rounded-lg overflow-hidden"
            >
              <Image
                src="https://res.cloudinary.com/ds1npuv1v/image/upload/v1742046123/college_txe5e6.jpg"
                alt="School campus"
                fill
                className="object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section id="map" className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Find Us
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our school is conveniently located in Pokhara-23, with easy access
              from the city center.
            </p>
          </motion.div>

          <motion.div
            className="relative h-[400px] md:h-[500px] rounded-lg overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* This would be replaced with an actual Google Maps embed in a real implementation */}
            <div className="absolute inset-0 bg-muted flex items-center justify-center">
              <iframe
                allowFullScreen
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d825.3445011099096!2d83.87777393454633!3d28.245727844379992!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3995eb8fc0539743%3A0x21ffa69f15411a33!2sShree%20Janakalyan%20English%20School!5e1!3m2!1sne!2snp!4v1741882381476!5m2!1sne!2snp"
                width={1300}
                height={615}
                className="border-none rounded-lg"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </motion.div>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">From City Center</h3>
                  <p className="text-sm text-muted-foreground">
                    Approximately 15 minutes by car or 30 minutes by public
                    transportation from Pokhara city center.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Public Transportation</h3>
                  <p className="text-sm text-muted-foreground">
                    Service runs every 30 minutes.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Parking</h3>
                  <p className="text-sm text-muted-foreground">
                    Visitor parking is available on campus. Please check in at
                    the upon arrival.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-16 md:py-24 bg-muted">
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Send Us a Message
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Have a question or need more information? Fill out the form below
              and we&apos;ll get back to you as soon as possible.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <Tabs defaultValue="general" className="mb-2">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="general">General Inquiry</TabsTrigger>
                <TabsTrigger value="support">Support & Partnership</TabsTrigger>
                <TabsTrigger value="feedback">Feedback</TabsTrigger>
              </TabsList>
              <div className="space-y-4 bg-background/90 p-6 mt-5 rounded-lg">
                <TabsContent value="general">
                  <GeneralInquiryForm />
                </TabsContent>
                <TabsContent value="support">
                  <SupportContactForm />
                </TabsContent>
                <TabsContent value="feedback">
                  <FeedbackForm />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Connect With Us
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Follow us on social media to stay updated with the latest news,
              events, and activities at Shree Janakalyan Boarding School.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            {[
              {
                icon: <FaFacebook className="h-6 w-6" />,
                name: "Facebook",
                url: "#",
              },
              {
                icon: <FaInstagram className="h-6 w-6" />,
                name: "Instagram",
                url: "#",
              },
              {
                icon: <FaTwitter className="h-6 w-6" />,
                name: "Twitter",
                url: "#",
              },
              {
                icon: <FaYoutube className="h-6 w-6" />,
                name: "YouTube",
                url: "#",
              },
            ].map((social, index) => (
              <motion.a
                key={social.name}
                href={social.url}
                className="flex flex-col items-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-3 text-primary">
                  {social.icon}
                </div>
                <span className="text-sm font-medium">{social.name}</span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Ready to Join Our School Family?
            </h2>
            <motion.p
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              We invite you to visit our campus, meet our teachers, and
              experience the vibrant learning environment at Shree Janakalyan
              Boarding School.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Button size="lg" variant="secondary" asChild>
                <Link href="/online-admission">Apply for Admission</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-primary-foreground/10 hover:bg-primary-foreground/70"
                asChild
              >
                <a href="#contact-form">Contact Us Today</a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

const API_URL = "/api";

const FeedbackForm = () => {
  const posthog = usePostHog();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const form = useForm<FeedbackFormValues>({
    mode: "onChange",
    resolver: customZodResolver(feedbackFormSchema as any),
    defaultValues: {
      type: "feedback", // Explicitly set the type
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      relationship: "parent",
      message: "",
    },
  });

  const onSubmit = async (data: FeedbackFormValues) => {
    try {
      setIsPending(true);
      const response = await fetch(`${API_URL}/contact/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Failed to send message");
      }
      posthog.capture("contact_feedback_submitted", {
        relationship: data.relationship,
        has_phone: !!data.phone,
      });
      form.reset();
      toast("Message Sent");
      setFormSubmitted(true);
      setIsPending(false);
    } catch (error) {
      console.error("Error sending message:", error);
      posthog.capture("contact_feedback_failed");
      toast("Failed to send message");
      setIsPending(false);
    }
  };

  if (formSubmitted) {
    return (
      <motion.div
        className="text-center py-8"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", damping: 15 }}
      >
        <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
          <CheckCircle2 className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold mb-4">Message Sent Successfully!</h3>
        <p className="text-muted-foreground mb-6">
          Thank you for contacting Shree Janakalyan Boarding School. We have
          received your message and will get back to you as soon as possible.
        </p>
        <Button onClick={() => setFormSubmitted(false)}>
          Send Another Message
        </Button>
      </motion.div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <motion.div
          className="space-y-6"
          initial="hidden"
          animate="show"
          variants={container}
        >
          <motion.div className="grid grid-cols-2 gap-4" variants={item}>
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your first name" {...field} />
                  </FormControl>
                  <FormMessage className="text-destructive" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your last name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </motion.div>

          <motion.div className="space-y-2" variants={item}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Enter your email address"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </motion.div>

          <motion.div className="space-y-2" variants={item}>
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your phone number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </motion.div>

          <motion.div className="space-y-2" variants={item}>
            <FormItem>
              <FormLabel>Your Relationship to the School</FormLabel>
              <RadioGroup
                defaultValue="parent"
                className="flex flex-col space-y-2"
              >
                <FormField
                  control={form.control}
                  name="relationship"
                  render={({ field }) => (
                    <FormControl>
                      <RadioGroup
                        defaultValue={field.value}
                        onValueChange={field.onChange}
                        className="flex flex-col space-y-2"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="parent" id="parent" />
                          <Label htmlFor="parent">Parent</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="student" id="student" />
                          <Label htmlFor="student">Student</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="alumni" id="alumni" />
                          <Label htmlFor="alumni">Alumni</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="other" id="other" />
                          <Label htmlFor="other">Other</Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </RadioGroup>
              <FormMessage />
            </FormItem>
          </motion.div>

          <motion.div className="space-y-2" variants={item}>
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter your message"
                      rows={5}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </motion.div>

          <motion.div variants={item}>
            <Button
              type="submit"
              className="w-full flex items-center gap-2"
              disabled={!form.formState.isValid || isPending}
            >
              {isPending ? (
                <div className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </div>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send Message
                </>
              )}
            </Button>
          </motion.div>
        </motion.div>
      </form>
    </Form>
  );
};
